// Typed fetch wrapper for the SPA. Always sends cookies (session cookie is
// HttpOnly + SameSite=Strict). Surfaces the server's error envelope as a
// thrown ApiError so React Query treats it as a failed query.

export interface ApiEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiErrorBody {
  error: { code: string; message: string; correlation_id?: string };
}

export class ApiError extends Error {
  status: number;
  code: string;
  correlationId?: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.correlationId = body.error.correlation_id;
  }
}

// TODO: custom domain granted.commondoor.ca to be
// configured via Cloudflare dashboard before pilot.
const BASE = '/api/v1';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const isJson = res.headers.get('content-type')?.includes('application/json');
  if (!res.ok) {
    if (isJson) {
      throw new ApiError(res.status, (await res.json()) as ApiErrorBody);
    }
    throw new ApiError(res.status, { error: { code: 'unknown', message: res.statusText } });
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<ApiEnvelope<T>>('GET', path),
  post: <T>(path: string, body?: unknown) => request<ApiEnvelope<T>>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<ApiEnvelope<T>>('PATCH', path, body),
  delete: <T>(path: string) => request<ApiEnvelope<T>>('DELETE', path),
};
